/**
 * Formatting helpers ported from the demo (`fmt`, `dmy`) so documents and
 * verdict pages render byte-identical strings to the reference.
 */

/** Demo `fmt`: en-US grouping, fixed decimals. `fmt(1234.5) === "1,234.50"`. */
export function fmt(value: number | string | null | undefined, decimals = 2): string {
  return Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** Money with an optional currency code prefix; use with `tabular` class for column alignment. */
export function money(
  value: number | string | null | undefined,
  currency?: string,
  decimals = 2,
): string {
  const s = fmt(value, decimals);
  return currency ? `${currency} ${s}` : s;
}

/** Grams to 3 dp (demo shows purity/weights with `toFixed(3)` in several places). */
export function grams(value: number | string | null | undefined, decimals = 3): string {
  return fmt(value, decimals);
}

/** Purity as a 3-dp ratio string, e.g. 0.916. */
export function purity(value: number | string | null | undefined): string {
  return Number(value || 0).toFixed(3);
}

/** Demo `dmy`: ISO `YYYY-MM-DD…` → `DD/MM/YYYY`. Empty input → "". */
export function dmy(iso: string | null | undefined): string {
  if (!iso) return "";
  const s = String(iso);
  return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
}

/** Demo `secCode`: first eight hex chars of the signature as `XXXX-XXXX`. */
export function secCode(signature: string): string {
  return `${signature.slice(0, 4)}-${signature.slice(4, 8)}`.toUpperCase();
}

/** Compact integer with grouping, no decimals. */
export function int(value: number | string | null | undefined): string {
  return fmt(value, 0);
}

/** Percentage with up to 1 dp. */
export function pct(value: number | null | undefined, decimals = 1): string {
  return `${Number(value || 0).toFixed(decimals)}%`;
}

/**
 * Human relative time: "just now", "5 min ago", "3 h ago", "2 d ago", "in 4 d".
 * Falls back to `dmy` beyond 30 days; empty input → "—".
 */
export function relativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = t - now.getTime();
  const abs = Math.abs(diff);
  const past = diff < 0;
  const unit = (n: number, u: string) => (past ? `${n} ${u} ago` : `in ${n} ${u}`);
  if (abs < 45_000) return "just now";
  if (abs < 3_600_000) return unit(Math.round(abs / 60_000), "min");
  if (abs < 86_400_000) return unit(Math.round(abs / 3_600_000), "h");
  if (abs < 30 * 86_400_000) return unit(Math.round(abs / 86_400_000), "d");
  return dmy(iso);
}

/** ISO → `DD/MM/YYYY HH:MM` in the viewer's local time zone. Empty input → "". */
export function dmyTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
